import { Container, Grid, Stack, Text } from "basis";
import { formatAmount } from "library/moneyUtils";
import { FC } from "react";
import { PAYMENT_TYPES } from "./constants";
import { jointAccountOptions } from "./DirectDebitForm";

export const jointAccountMap = jointAccountOptions.reduce(
  (acc, { label, value }) => {
    acc[value] = label;

    return acc;
  },
  {},
);

interface Props {
  accountName?: string;
  jointAccount?: string;
  bankName?: string;
  bsb?: string;
  accountNumber?: string;
  paymentType: string;
  paymentAmount?: string;
  prfi: boolean;
}

export const DirectDebitDetails: FC<Props> = ({
  accountName,
  jointAccount,
  bankName,
  bsb,
  accountNumber,
  paymentType,
  paymentAmount,
  prfi,
}: Props) => {
  const fullStatementTitle = prfi
    ? "Payment to reduce future interest"
    : "Full statement closing balance";
  const fullStatementDescription = prfi
    ? "Includes all interest bearing purchases and interest free plans expiring each month."
    : "";

  return (
    <Container hasBorder padding="4" testId="direct-debit-details">
      <Grid rowsGap="6" rowsGap-sm="8">
        {accountName !== undefined && (
          <Grid cols="4" cols-sm="8" colsGap="30px" rowsGap="1">
            <Grid.Item colSpan="all" colSpan-sm="0-2">
              <Text color="grey.t75">Name of account to be debited</Text>
            </Grid.Item>
            <Grid.Item colSpan="all" colSpan-sm="3-7">
              <Text>
                <strong>{accountName}</strong>
              </Text>
            </Grid.Item>
          </Grid>
        )}
        {jointAccount !== undefined && (
          <Grid cols="4" cols-sm="8" colsGap="30px" rowsGap="1">
            <Grid.Item colSpan="all" colSpan-sm="0-2">
              <Text color="grey.t75">Joint account</Text>
            </Grid.Item>
            <Grid.Item colSpan="all" colSpan-sm="3-7">
              <Text>
                <strong>{jointAccountMap[jointAccount]}</strong>
              </Text>
            </Grid.Item>
          </Grid>
        )}
        {bankName !== undefined && (
          <Grid cols="4" cols-sm="8" colsGap="30px" rowsGap="1">
            <Grid.Item colSpan="all" colSpan-sm="0-2">
              <Text color="grey.t75">Bank name</Text>
            </Grid.Item>
            <Grid.Item colSpan="all" colSpan-sm="3-7">
              <Text>
                <strong>{bankName}</strong>
              </Text>
            </Grid.Item>
          </Grid>
        )}
        <Grid cols="4" cols-sm="8" colsGap="30px" rowsGap="1">
          <Grid.Item colSpan="all" colSpan-sm="0-2">
            <Text color="grey.t75">BSB</Text>
          </Grid.Item>
          <Grid.Item colSpan="all" colSpan-sm="3-7">
            <Text>
              <strong>{bsb}</strong>
            </Text>
          </Grid.Item>
        </Grid>
        <Grid cols="4" cols-sm="8" colsGap="30px" rowsGap="1">
          <Grid.Item colSpan="all" colSpan-sm="0-2">
            <Text color="grey.t75">Account number</Text>
          </Grid.Item>
          <Grid.Item colSpan="all" colSpan-sm="3-7">
            <Text>
              <strong>{accountNumber}</strong>
            </Text>
          </Grid.Item>
        </Grid>
        <Grid cols="4" cols-sm="8" colsGap="30px" rowsGap="1">
          <Grid.Item colSpan="all" colSpan-sm="0-2">
            <Text color="grey.t75">Monthly payment amount</Text>
          </Grid.Item>
          <Grid.Item colSpan="all" colSpan-sm="3-7">
            {[
              PAYMENT_TYPES.MINIMUM_MONTHLY,
              PAYMENT_TYPES.FULL_CLOSING,
              PAYMENT_TYPES.FIXED,
              PAYMENT_TYPES.PRFI,
              PAYMENT_TYPES.PAYMENT_PLAN,
            ].includes(paymentType) && (
              <Text>
                <strong>
                  {paymentType === PAYMENT_TYPES.MINIMUM_MONTHLY
                    ? "Minimum monthly payment + any overdue and overlimit amount"
                    : paymentType === PAYMENT_TYPES.FULL_CLOSING ||
                      paymentType === PAYMENT_TYPES.PRFI
                    ? fullStatementTitle
                    : paymentType === PAYMENT_TYPES.FIXED ||
                      paymentType === PAYMENT_TYPES.PAYMENT_PLAN
                    ? formatAmount(paymentAmount, { withCents: true })
                    : null}
                </strong>
              </Text>
            )}
            {[
              PAYMENT_TYPES.FIXED,
              PAYMENT_TYPES.PRFI,
              PAYMENT_TYPES.PAYMENT_PLAN,
            ].includes(paymentType) && (
              <Text>
                {paymentType === PAYMENT_TYPES.FIXED ||
                paymentType === PAYMENT_TYPES.PAYMENT_PLAN
                  ? "or your minimum monthly payment (whichever is greater); or statement closing balance (whichever is lower)."
                  : paymentType === PAYMENT_TYPES.PRFI
                  ? fullStatementDescription
                  : null}
              </Text>
            )}
          </Grid.Item>
        </Grid>
        <Grid cols="4" cols-sm="8" colsGap="30px" rowsGap="1">
          <Grid.Item colSpan="all" colSpan-sm="0-2">
            <Text color="grey.t75">Monthly debit date</Text>
          </Grid.Item>
          <Grid.Item colSpan="all" colSpan-sm="3-7">
            <Stack gap-sm="1">
              <Text>
                <strong>Starting from your next statement due date</strong>
              </Text>
            </Stack>
          </Grid.Item>
        </Grid>
      </Grid>
    </Container>
  );
};
